// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where the letters go (M7-10).
//
// `glyphs.ts` says what an `S` looks like in em units. This says where each one lands when you
// ask for `"SUPER JACKPOT"` centred at a point at a given size — the advance widths, the
// tracking, the alignment, and the one sign flip that decides whether the text reads the right
// way up.
//
// **It is shared because placement is half of what a typeface is.** Two clients with the same
// letterforms and different centring produce two different wordmarks, and the difference is
// invisible in a code review and obvious side by side. The C# twin is
// `core/cs/HccPinball.Core/Art/Typeset.cs`, and a parity fixture holds them together.
//
// The output is deliberately dumb: flat runs of x, y pairs in **pixels**, ready to be walked with
// a move-to and a series of line-tos. A browser strokes them onto a canvas and the native
// rasteriser walks the same numbers into a byte buffer. Neither is given a chance to disagree
// about layout, because neither does any.

import { GLYPHS, GLYPH_METRICS, TOFU, type Glyph } from './glyphs'

export type TextAlign = 'left' | 'center' | 'right'

/**
 * Which part of the text lands on the anchor point.
 *
 * `middle` centres the *capital* box rather than the full em. That is the difference between a
 * lane letter sitting in its insert and a lane letter sitting slightly high in it: descenders and
 * ascenders are part of an em box and are not part of what an eye reads as the centre of a word.
 */
export type TextBaseline = 'alphabetic' | 'middle' | 'cap'

export interface TypesetOptions {
  /** The em, in pixels. A capital stands `size × 0.70` tall, as it would in any font. */
  readonly size: number
  readonly align?: TextAlign
  readonly baseline?: TextBaseline
  /** Extra space between glyphs, em. Defaults to the face's own. */
  readonly tracking?: number
  /**
   * False on a surface whose canvas has been flipped so that +y runs up the picture.
   *
   * A playfield's art program addresses the machine the way the machine definition does, so the
   * whole surface is drawn flipped — and a glyph drawn without accounting for that comes out
   * upside down. It is also *legible* upside down, which is why it survives a look at a
   * screenshot: everything else is correct, so the eye reports "the art is a bit odd".
   */
  readonly yDown?: boolean
}

/** The glyph for a character, or the box that says one is missing. */
export function glyphFor(character: string): Glyph {
  return GLYPHS[character] ?? GLYPHS[TOFU]!
}

/** How wide a string is, in pixels, at a size. */
export function measureText(text: string, size: number, tracking: number = GLYPH_METRICS.tracking): number {
  if (text.length === 0) return 0
  let em = 0
  for (const character of text) em += glyphFor(character).advance + tracking
  // The trailing tracking is not part of the word: leaving it in shifts every centred string
  // half a tracking to the left, which is small, uniform, and exactly the kind of thing nobody
  // ever finds by looking.
  return (em - tracking) * size
}

/**
 * A string as positioned strokes, in pixels.
 *
 * Each run is a flat `[x0, y0, x1, y1, …]` to be stroked as one open polyline. An empty result is
 * a string with nothing drawable in it — a space, or an empty string — and is not an error.
 */
export function typeset(text: string, x: number, y: number, options: TypesetOptions): number[][] {
  const size = options.size
  const tracking = options.tracking ?? GLYPH_METRICS.tracking
  const align = options.align ?? 'left'
  const baseline = options.baseline ?? 'alphabetic'
  // Screen y grows downward on an ordinary canvas and upward on a flipped one, and this single
  // sign is the whole of the difference.
  const sign = options.yDown === false ? 1 : -1

  const width = measureText(text, size, tracking)
  let penX = x
  if (align === 'center') penX = x - width / 2
  else if (align === 'right') penX = x - width

  let baselineY = y
  if (baseline === 'middle') baselineY = y - sign * (GLYPH_METRICS.cap / 2) * size
  else if (baseline === 'cap') baselineY = y - sign * GLYPH_METRICS.cap * size

  const runs: number[][] = []
  for (const character of text) {
    const glyph = glyphFor(character)
    for (const stroke of glyph.strokes) {
      const run: number[] = []
      for (let i = 0; i + 1 < stroke.length; i += 2) {
        run.push(penX + stroke[i]! * size, baselineY + sign * stroke[i + 1]! * size)
      }
      if (run.length >= 2) runs.push(run)
    }
    penX += (glyph.advance + tracking) * size
  }
  return runs
}

/**
 * How thick to stroke text at a size, in pixels.
 *
 * A separate function because a wordmark wants a heavier pen than a label, and because the
 * weight has to be the *same fraction of the em* on both clients or the same word reads bolder in
 * a headset than in a browser.
 */
export function strokeWidth(size: number, weight: number = GLYPH_METRICS.weight): number {
  // Never thinner than a pixel: a hairline that lands between two samples on a 2048-pixel
  // playfield texture disappears in patches, which reads as a broken font rather than a thin one.
  const width = size * weight
  return width < 1 ? 1 : width
}
